import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Users, Eye, MousePointer, Activity, Gauge, BarChart3, ThumbsUp, ThumbsDown } from 'lucide-react';
import type { AnalyticsSummary } from '../../types/analytics';

interface MetricCardsProps {
  summary: AnalyticsSummary;
  isLoading: boolean;
}

interface MetricDef {
  label: string;
  key: keyof AnalyticsSummary;
  icon: React.ReactNode;
  format?: (v: number | null) => string;
}

const metrics: MetricDef[] = [
  { label: 'Unique Visitors', key: 'uniqueVisitors', icon: <Users className="w-5 h-5" /> },
  { label: 'Avg DAU', key: 'avgDAU', icon: <Activity className="w-5 h-5" /> },
  { label: 'Latest WAU', key: 'latestWAU', icon: <BarChart3 className="w-5 h-5" /> },
  { label: 'Stickiness', key: 'stickiness', icon: <Gauge className="w-5 h-5" />, format: (v) => v != null ? `${(v * 100).toFixed(1)}%` : 'N/A' },
  { label: 'Total Sessions', key: 'totalSessions', icon: <MousePointer className="w-5 h-5" /> },
  { label: 'Activity Views', key: 'activityViews', icon: <Eye className="w-5 h-5" /> },
  { label: 'Activity Likes', key: 'activityLikes', icon: <ThumbsUp className="w-5 h-5" /> },
  { label: 'Activity Dislikes', key: 'activityDislikes', icon: <ThumbsDown className="w-5 h-5" /> },
  { label: 'Activities Saved', key: 'activitiesSaved', icon: <Activity className="w-5 h-5" /> },
  { label: 'Expert Tips Viewed', key: 'expertTipsViewed', icon: <Eye className="w-5 h-5" /> },
  { label: 'Expert Tips Page Views', key: 'expertTipsPageViews', icon: <Eye className="w-5 h-5" /> },
  { label: 'Weather Check-ins', key: 'weatherCheckins', icon: <Activity className="w-5 h-5" /> },
  { label: 'Assessments Started', key: 'assessmentsStarted', icon: <Activity className="w-5 h-5" /> },
  { label: 'Assessments Completed', key: 'assessmentsCompleted', icon: <Activity className="w-5 h-5" /> },
];

export default function MetricCards({ summary, isLoading }: MetricCardsProps) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
      {metrics.map((metric, index) => (
        <motion.div
          key={metric.key}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.05 }}
        >
          <Card className="bg-admin-dark-800 border-admin-slate-300/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2 text-admin-slate-500">
                {metric.icon}
                <span className="text-xs truncate">{metric.label}</span>
              </div>
              {isLoading ? (
                <div className="h-7 w-16 bg-admin-dark-700 animate-pulse rounded" />
              ) : (
                <p className="text-xl font-bold text-admin-slate-400">
                  {metric.format
                    ? metric.format(summary[metric.key] as number | null)
                    : (summary[metric.key] ?? 0).toLocaleString()}
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </div>
  );
}
